import { getValidationProblemDetailsFromBlob } from '@/api/axios';
import type { ValidationProblemDetails } from '@/api/model';
import { ref } from 'vue';

/**
 * Unterdürckt die globale Fehlerbehandlung (z.B. Anzeige der Fehlerseite) für Fehler, die lokal behandelt werden sollen (z.B. Validierungsfehler bei Formularen).
 * Unterdrückt außerdem das Senden von Fehlern an Sentry (siehe beforeSend callback in src/main.js).
 */
export const SKIP_GLOBAL_ERROR_HANDLING_FLAG = '_skipGlobalErrorHandling';

/**
 * Liefert die zusammengefügte Fehlermeldung für ein Feld (Semikolon-getrennt).
 */
export function getErrorMessage(formErrors: { [key: string]: string[] }, feldname: string): string {
    const fehler = formErrors?.[feldname];
    return Array.isArray(fehler) ? fehler.join('\r\n') : '';
}

/**
 * Composable für zentrale Fehlerbehandlung
 *
 * Behandelt:
 * - ValidationProblemDetails (JSON und Blob) → lokale Anzeige in ValidationSummary
 * - 401 Unauthorized → ignorieren (axios interceptor übernimmt Token-Refresh)
 * - Alle anderen Fehler → weiterwerfen für globalen Handler
 */
export function useErrorHandler() {
    const validationFehler = ref<ValidationProblemDetails | null>(null);

    // Liefert etwaige Validierungsfehler aufbereitet, sodass sie direkt mittels v-bind ans FormField gebunden werden können.
    const getFehlerModel = (feldname: string) => {
        const msg = getErrorMessage(validationFehler.value?.errors, feldname);
        return {
            invalid: !!msg,
            errorMessage: msg
        };
    };

    const behandleFehler = async (error: any): Promise<void> => {
        // Reset
        validationFehler.value = null;

        const status = error?.response?.status;

        // Flag am Error-Objekt zum Unterdrücken der globalen Fehlerseite
        if (error?.[SKIP_GLOBAL_ERROR_HANDLING_FLAG]) {
            return;
        }

        // ValidationProblemDetails prüfen (nur bei Status 400)
        const response = error?.response;
        if (response?.status === 400) {
            const data = response.data;

            // Blob-Response?
            if (data instanceof Blob) {
                validationFehler.value = await getValidationProblemDetailsFromBlob(data);
                return;
            }

            // JSON-Response?
            if (data && typeof data === 'object' && 'errors' in data) {
                validationFehler.value = data as ValidationProblemDetails;
                return;
            }
        }

        // Alles andere weiterwerfen für globalen Handler
        throw error;
    };

    return {
        validationFehler,
        behandleFehler,
        getFehlerModel
    };
}
